import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Quote } from "lucide-react";
import { brothers } from "@/data/brothers";
import BrotherImage from "@/components/BrotherImage";

export default function Brothers() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div data-testid="brothers-page" className="pt-20">
      {/* Header */}
      <section className="bg-brand-green py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-brand-sand text-sm uppercase tracking-[0.3em] font-semibold mb-5">
            Our Journey &amp; Brothers
          </p>
          <h1 className="font-serif font-light text-brand-bg text-4xl sm:text-6xl leading-tight max-w-3xl">
            The human element of a radical hope
          </h1>
          <p className="text-brand-bg/75 text-lg mt-6 max-w-2xl leading-relaxed">
            From Tallahassee to Uganda to Kenya, these are the testimonies of the brothers building
            a global fraternity of presence, accompaniment, and restoration.
          </p>
        </div>
      </section>

      {/* Testimonies */}
      <section className="py-20 sm:py-28 bg-brand-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 sm:space-y-32">
          {brothers.map((b, i) => (
            <motion.article
              key={b.slug}
              id={b.slug}
              data-testid={`brother-section-${b.slug}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center scroll-mt-28 ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-5">
                <div className="rounded-2xl overflow-hidden aspect-[4/5] border border-brand-line shadow-sm">
                  <BrotherImage src={b.image} name={b.name} position={b.imagePos} />
                </div>
              </div>

              <div className="lg:col-span-7">
                <p className="inline-flex items-center gap-2 text-brand-terracotta text-xs uppercase tracking-[0.2em] font-semibold mb-4">
                  <MapPin className="w-4 h-4" /> {b.location}
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl text-brand-ink mb-2">{b.name}</h2>
                <p className="text-brand-muted font-medium mb-6">{b.role}</p>
                <p className="text-brand-ink text-lg leading-relaxed mb-6">{b.lead}</p>

                <blockquote className="relative bg-brand-surface border-l-4 border-brand-terracotta rounded-r-xl p-6 mb-6">
                  <Quote className="w-6 h-6 text-brand-sand mb-2" />
                  <p className="font-serif italic text-xl text-brand-ink leading-snug">"{b.quote}"</p>
                </blockquote>

                {b.body.map((para, idx) => (
                  <p key={`${b.slug}-para-${idx}`} className="text-brand-muted leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
